import React, { useState, useEffect } from 'react';
import { X, Download, Check, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { ExerciseTemplate } from '../types';
import { useTemplates } from '../hooks/useTemplates';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface ImportTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  workoutId: number;
  clientId: number;
  clientName: string;
  existingExerciseIds: number[];
  onImportSuccess?: () => void;
}

const ImportTemplateModal: React.FC<ImportTemplateModalProps> = ({
  isOpen,
  onClose,
  workoutId,
  clientId,
  clientName,
  existingExerciseIds,
  onImportSuccess
}) => {
  const { templates, isLoading, refetch } = useTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<ExerciseTemplate | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'date' | 'name'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSortClick = (field: 'date' | 'name') => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'name' ? 'asc' : 'desc');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate(null);
      setSearchQuery('');
      setSortField('date');
      setSortDirection('desc');
    } else {
      // Принудительно обновляем данные шаблонов при открытии модального окна
      refetch();
    }
  }, [isOpen, refetch]);

  const handleTemplateSelect = (template: ExerciseTemplate) => {
    setSelectedTemplate(template);
  };

  // Фильтрация шаблонов по поисковому запросу
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Сортировка отфильтрованных шаблонов
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const compare =
      sortField === 'name'
        ? a.name.localeCompare(b.name, 'ru')
        : new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return sortDirection === 'asc' ? compare : -compare;
  });

  const handleImport = async () => {
    if (!selectedTemplate) return;

    setIsImporting(true);
    try {
      const result = await apiService.importTemplateToWorkout(workoutId, selectedTemplate.id, clientId);
      
      let message = result.message;
      if (result.skipped_count > 0) {
        message += ` (${result.skipped_count} упражнений уже есть у клиента)`;
      }
      
      toast.success(message);
      onClose();
      onImportSuccess?.();
      
      // Обновляем данные шаблонов после успешного импорта
      refetch();
    } catch (error) {
      console.error('Ошибка импорта шаблона:', error);
      toast.error('Ошибка при импорте шаблона');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Заголовок */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Download className="h-6 w-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Импорт шаблона
                </h2>
                <p className="text-sm text-gray-600">
                  Выберите шаблон для добавления упражнений к {clientName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Строка поиска */}
          <div className="relative">
            <input
              type="text"
              placeholder="Поиск по названию шаблона..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Плашки сортировки */}
          <div className="flex items-center space-x-2 mt-3">
            <span className="text-xs text-gray-500">Сортировка:</span>
            <button
              type="button"
              onClick={() => handleSortClick('name')}
              className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                sortField === 'name'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>Название</span>
              {sortField === 'name' && (
                sortDirection === 'asc'
                  ? <ArrowUp className="h-3 w-3" />
                  : <ArrowDown className="h-3 w-3" />
              )}
            </button>
            <button
              type="button"
              onClick={() => handleSortClick('date')}
              className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                sortField === 'date'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>Дата</span>
              {sortField === 'date' && (
                sortDirection === 'asc'
                  ? <ArrowUp className="h-3 w-3" />
                  : <ArrowDown className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>

        {/* Содержимое */}
        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Загрузка шаблонов...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Шаблоны не найдены
                  </h3>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? `По запросу "${searchQuery}" ничего не найдено`
                      : 'Нет доступных шаблонов'
                    }
                  </p>
                </div>
              ) : (
                sortedTemplates.map((template) => {
                  const isSelected = selectedTemplate?.id === template.id;
                  
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-medium text-gray-900">
                              {template.name}
                            </h3>
                            {isSelected && (
                              <Check className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          
                          {template.description && (
                            <p className="text-sm text-gray-600 mb-3">
                              {template.description}
                            </p>
                          )}

                          {/* Список упражнений */}
                          <div className="mt-3 space-y-1">
                            {template.template_exercises.map((te, index) => (
                              <div
                                key={te.id}
                                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700"
                              >
                                {index + 1}. {te.exercise?.name || 'Упражнение удалено'} 
                                ({te.sets}×{te.reps}
                                {te.weight && `, ${te.weight}кг`})
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Кнопки */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              {selectedTemplate && (
                <span>
                  Выбран: <span className="font-medium">{selectedTemplate.name}</span>
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Отмена
              </button>
              <button
                onClick={handleImport}
                disabled={!selectedTemplate || isImporting}
                className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  selectedTemplate && !isImporting
                    ? 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                }`}
              >
                {isImporting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Импорт...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Download className="h-4 w-4" />
                    <span>Импортировать</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportTemplateModal; 